/**
 * UpgradeModalA - Modal Variant (v2)
 * 
 * Clean centered modal overlay for Pro upgrade funnel.
 * 
 * v2 Changes:
 * - 2 pricing cards (Monthly + Lifetime) instead of 3
 * - Trial toggle pre-enabled
 * - Trust badges (No payment today, Cancel in 2 taps)
 * - Outcome-focused benefits
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { BenefitsList } from './shared/BenefitsList';
import { PricingCard } from './shared/PricingCard';
import { PricingPlan, TRUST_SIGNALS } from './shared/constants';
import { useLanguage } from '@/i18n';
import './UpgradeModalA.css';

interface UpgradeModalAProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan: (plan: PricingPlan) => void;
  triggerSource?: 'sync-toggle' | 'second-device' | '30-day-prompt';
}

export function UpgradeModalA({ 
  isOpen, 
  onClose, 
  onSelectPlan,
  triggerSource = 'sync-toggle',
}: UpgradeModalAProps) {
  const { t } = useLanguage();
  // Trial toggle state - pre-enabled by default (v2 psychology)
  const [trialEnabled, setTrialEnabled] = useState(true);

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Dynamic headline based on trigger (v2 copy)
  const getHeadline = () => {
    switch (triggerSource) {
      case 'second-device':
        return t('funnel.modal_title_sync');
      case '30-day-prompt':
        return t('funnel.modal_title_ready');
      default:
        return t('funnel.modal_title_default');
    }
  };

  const getSubheadline = () => {
    switch (triggerSource) {
      case 'second-device':
        return t('funnel.modal_sub_sync');
      case '30-day-prompt':
        return t('funnel.modal_sub_ready');
      default:
        return t('funnel.modal_sub_default');
    }
  };

  // Handle plan selection with trial toggle
  const handleSelectPlan = (plan: PricingPlan) => {
    // If monthly and trial not enabled, could show different flow
    // For now, trial is always enabled by default
    onSelectPlan(plan);
  };

  return createPortal(
    <div className="upgrade-modal-backdrop" onClick={handleBackdropClick}>
      <div 
        className="upgrade-modal" 
        role="dialog" 
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
      >
        {/* Close button */}
        <button
          className="upgrade-modal-close"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Decorative glow */}
        <div className="upgrade-modal-glow" aria-hidden="true" />

        {/* Pro badge */}
        <div className="upgrade-modal-badge">
          <span className="badge-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
          </span>
          NinjaCash Pro
        </div>

        {/* Header */}
        <h2 id="upgrade-modal-title" className="upgrade-modal-title">
          {getHeadline()}
        </h2>
        <p className="upgrade-modal-subtitle">
          {getSubheadline()}
        </p>

        {/* Benefits (v2: outcome-focused) */}
        <BenefitsList variant="compact" className="upgrade-modal-benefits" />

        {/* v2: Two pricing cards side-by-side */}
        <div className="upgrade-modal-cards">
          <PricingCard 
            plan="monthly" 
            highlighted={trialEnabled}
            onSelect={handleSelectPlan}
            variant="compact"
          />
          <PricingCard 
            plan="lifetime" 
            onSelect={handleSelectPlan}
            variant="compact"
          />
        </div>

        {/* v2: Trial toggle */}
        <label className="upgrade-modal-toggle">
          <input 
            type="checkbox" 
            checked={trialEnabled}
            onChange={(e) => setTrialEnabled(e.target.checked)}
          />
          <span className="toggle-slider" />
          <span className="toggle-label">
            {t('funnel.trial_enabled')}
          </span>
        </label>

        {/* v2: Trust signals */}
        <div className="upgrade-modal-trust">
          <span className="trust-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            {t(TRUST_SIGNALS.securePayment)}
          </span>
          <span className="trust-divider">|</span>
          <span className="trust-item">{t(TRUST_SIGNALS.cancelAnytime)}</span>
        </div>

        {/* Dismiss link */}
        <button className="upgrade-modal-dismiss" onClick={onClose}>
          {t('funnel.no_thanks')}
        </button>
      </div>
    </div>,
    document.body
  );
}
